import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FAMILY_SETTINGS_QUERY_KEY } from '@/api/queryKeys';
import { DetailLayout } from '@/components/ProfileMenu/DetailLayout';
import { useCreatePhone } from '@/components/ProfileMenu/hooks/useCreatePhone';
import { usePhoneForm } from '@/components/ProfileMenu/hooks/usePhoneForm';
import { useUpdatePhone } from '@/components/ProfileMenu/hooks/useUpdatePhone';
import { Icon } from '@/components/Icons';
import type { AddPhoneContext, FamilyMember } from '@/types/user';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { submitOnEnter } from '@/utils/form';
import { personTypeIcons } from '@/utils/person';
import { isBlank } from '@/utils/text';
import { isValidPhoneNumber } from '@/utils/validation';

interface Props {
  member?: FamilyMember;
  addContext?: AddPhoneContext;
  onBack: () => void;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
}

export const PhoneView: React.FC<Props> = ({
  member,
  addContext,
  onBack,
  onClose,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    isFamilyMember,
    personType,
    targetUserPublicId,
    displayName,
    currentPhone,
    buildMessage,
  } = usePhoneForm(member, addContext);

  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [message, setMessage] = useState(() => buildMessage(false));

  const isSaveDisabled = isBlank(message) || (!member && isBlank(phone));

  const { updatePhone, isPending: isUpdating } = useUpdatePhone();
  const { createPhone, isPending: isCreating } = useCreatePhone();
  const isPending = isUpdating || isCreating;

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const wasEmpty = isBlank(phone);
    const isEmpty = isBlank(value);

    setPhone(value);

    if (phoneError) {
      setPhoneError('');
    }

    if (member && isEmpty) {
      setMessage(buildMessage(true));
    } else if (member && wasEmpty) {
      setMessage(buildMessage(false));
    }
  };

  const handleSave = async () => {
    if (isPending || isSaveDisabled || !targetUserPublicId) {
      return;
    }

    if (!isFamilyMember && !isValidPhoneNumber(phone)) {
      setPhoneError(t('changePhone.invalidFormatError'));
      return;
    }

    setPhoneError('');

    const request = {
      targetUserPublicId,
      phoneNumber: phone.trim(),
      message: message.trim(),
    };

    try {
      await (addContext
        ? createPhone({ personType: addContext.personType, ...request })
        : updatePhone(request));

      if (isFamilyMember) {
        queryClient.invalidateQueries({ queryKey: FAMILY_SETTINGS_QUERY_KEY });
      }

      onSuccess();
    } catch (requestError) {
      logRequestError(ErrorMessage.ChangePhone, requestError);

      if (
        isFamilyMember &&
        isAxiosError(requestError) &&
        requestError.response?.status === 400
      ) {
        setPhoneError(t('changePhone.numberValidationError'));
        return;
      }

      onError();
    }
  };

  const messageField = (
    <>
      <label
        className={`${addContext ? '' : 'mt-5 '}mb-2 text-body-14 text-foreground md:text-body-16`}
      >
        {t('changePhone.messageLabel')}
      </label>
      <textarea
        value={message}
        readOnly={!!addContext}
        onChange={(event) => setMessage(event.target.value)}
        placeholder={t('changePhone.messagePlaceholder')}
        className={`min-h-[160px] resize-none rounded-xl border border-transparent bg-on-primary-surface p-4 text-body-16 outline-none placeholder:text-body-16 placeholder:text-subtle-foreground ${addContext ? 'cursor-default text-subtle-foreground' : 'text-foreground'}`}
      />
    </>
  );

  return (
    <DetailLayout
      title={t('changePhone.title')}
      saveLabel={t('global.send')}
      isSaveDisabled={isSaveDisabled}
      isSaving={isPending}
      onBack={onBack}
      onClose={onClose}
      onSave={handleSave}
    >
      <div className="mt-4 mb-5 flex items-center gap-3 border-b border-divider pb-5 md:mt-0 md:border-border">
        {personType && (
          <Icon
            icon={personTypeIcons[personType]}
            className="size-10 shrink-0"
          />
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <p
            className={`truncate text-body-16 text-foreground ${member ? '' : 'capitalize'}`}
          >
            {displayName}
          </p>
          {currentPhone && (
            <p className="truncate text-body-14 text-muted-foreground md:text-caption-12">
              {currentPhone}
            </p>
          )}
        </div>
      </div>

      {addContext && messageField}

      <label
        className={`${addContext ? 'mt-5 ' : ''}mb-2 text-body-14 text-foreground md:text-body-16`}
      >
        {t('changePhone.label')}
      </label>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="off"
        value={phone}
        onChange={handlePhoneChange}
        onKeyDown={submitOnEnter(handleSave)}
        placeholder={t('changePhone.placeholder')}
        className="h-14 shrink-0 rounded-xl border border-transparent bg-on-primary-surface px-4 text-body-16 text-foreground outline-none placeholder:text-subtle-foreground md:h-12"
      />
      {phoneError && (
        <p className="mt-2 text-body-14 text-destructive">{phoneError}</p>
      )}

      {!addContext && messageField}

      <p className="mt-5 rounded-xl border border-divider p-4 text-body-14 text-muted-foreground">
        {t('changePhone.explanation')}
      </p>
    </DetailLayout>
  );
};
